import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { apiErr } from "../lib/api";

export default function Auth() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [role, setRole] = useState(params.get("role") === "employer" ? "employer" : "candidate");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [loading, setLoading] = useState(false);

  const go = (u) => nav(u.role === "employer" ? "/employer" : u.role === "admin" ? "/admin" : u.role === "flex" ? "/flex" : "/candidate");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u =
        mode === "login"
          ? await login(form.email, form.password)
          : await register({ ...form, role });
      toast.success(`Welcome${u.name ? ", " + u.name.split(" ")[0] : ""}!`);
      go(u);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-12 grid lg:grid-cols-2 gap-0 border border-[#0A0A0A] my-10">
      <div className="bg-[#0A0A0A] text-white p-10 hidden lg:flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            <img src="/darriker-logo.png" alt="Darriker" className="w-11 h-11 object-contain" />
            <div className="leading-none">
              <div className="font-display font-black text-3xl tracking-tighter">Darriker</div>
              <div className="font-mono2 text-xs uppercase tracking-wider text-[#9CA3AF] mt-1">GlobalHire<span className="text-[#3b6bff]">.AI</span></div>
            </div>
          </div>
          <p className="mt-6 text-[#9CA3AF]">
            The AI talent matching engine. Build an intelligence profile, get scored matches,
            and let the algorithm do the screening.
          </p>
        </div>
      </div>

      <div className="bg-white p-8 sm:p-10">
        <div className="flex border border-[#0A0A0A] mb-8">
          <button
            data-testid="tab-login"
            onClick={() => setMode("login")}
            className={`flex-1 py-2.5 text-sm font-medium ${mode === "login" ? "tab-active" : ""}`}
          >
            Log in
          </button>
          <button
            data-testid="tab-register"
            onClick={() => setMode("register")}
            className={`flex-1 py-2.5 text-sm font-medium ${mode === "register" ? "tab-active" : ""}`}
          >
            Register
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  data-testid="role-candidate"
                  onClick={() => setRole("candidate")}
                  className={`py-3 text-xs font-medium border ${role === "candidate" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#0A0A0A]"}`}
                >
                  Candidate
                </button>
                <button
                  type="button"
                  data-testid="role-employer"
                  onClick={() => setRole("employer")}
                  className={`py-3 text-xs font-medium border ${role === "employer" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#0A0A0A]"}`}
                >
                  Employer
                </button>
                <button
                  type="button"
                  data-testid="role-flex"
                  onClick={() => setRole("flex")}
                  className={`py-3 text-xs font-medium border ${role === "flex" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#0A0A0A]"}`}
                >
                  Flex Worker
                </button>
              </div>
              {role === "flex" && (
                <p className="font-mono2 text-xs text-[#4B5563] -mt-1">For part-time, contract, weekend & gig work. Free to list.</p>
              )}
              <Field label="Full name" testid="auth-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              {role === "employer" && (
                <Field label="Company" testid="auth-company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
              )}
            </>
          )}
          <Field label="Email" type="email" testid="auth-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
          <Field label="Password" type="password" testid="auth-password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required />

          <button
            type="submit"
            data-testid="auth-submit"
            disabled={loading}
            className="btn-ink w-full py-3 font-medium rounded-none disabled:opacity-50"
          >
            {loading ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
          </button>
        </form>
        <p className="mt-4 text-xs text-[#4B5563]">
          By continuing you agree to the Darriker terms. <Link to="/pricing" className="text-[#002FA7]">See plans</Link>.
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block">
      <span className="font-mono2 text-xs uppercase text-[#4B5563]">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
      />
    </label>
  );
}
